import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import SectionLabel from '@/components/generals/section-label'
import SplitReveal from '@/components/motion/split-reveal'
import ParallaxImage from '@/components/motion/parallax-image'
import { gsap, MOTION_QUERIES, useGSAP } from '@/lib/motion'
import { awards, whyPoints } from '@/constants/content'

// One team, five commitments, set against the studio itself. Each commitment's
// rule draws across as it arrives, like ticking items off a brief.
const Why: FC = (): ReactNode => {
    const listRef = useRef<HTMLOListElement>(null)

    useGSAP(() => {
        const list = listRef.current

        if (!list) return

        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.motion, () => {
            list.querySelectorAll<HTMLElement>('[data-point]').forEach(point => {
                const rule = point.querySelector('[data-rule]')
                const body = point.querySelectorAll('[data-point-body]')

                gsap.timeline({
                    scrollTrigger: {
                        trigger: point,
                        start: 'top 86%',
                        once: true
                    }
                })
                    .fromTo(rule, { scaleX: 0 }, { scaleX: 1, duration: 1.1, ease: 'expo.inOut' })
                    .fromTo(body, { autoAlpha: 0, x: -16 }, { autoAlpha: 1, x: 0, duration: 0.9, ease: 'expo.out', stagger: 0.08 }, 0.35)
            })
        })

        return () => mm.revert()
    }, { scope: listRef })

    return (
        <section id='why' data-scene='paper' aria-labelledby='why-title' className='px-5 py-28 md:px-8 md:py-44'>
            <div className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:col-span-5'>
                    <div className='relative lg:sticky lg:top-28'>
                        <ParallaxImage
                            src='/images/studio/office.avif'
                            alt='The Fit Design studio, with people working at desks on a wooden floor.'
                            className='aspect-[4/5]'
                            reveal='left'
                        />

                        <img
                            src='/images/awards/webflow-expert.avif'
                            alt='Webflow Expert Certification badge.'
                            loading='lazy'
                            decoding='async'
                            className='absolute -bottom-6 right-4 w-24 md:w-28'
                        />
                    </div>
                </div>

                <div className='lg:col-span-6 lg:col-start-7'>
                    <SectionLabel index='07'>
                        Why Fit Design
                    </SectionLabel>

                    <SplitReveal as='h2' id='why-title' className='type-h2 mt-8'>
                        One team for your strategy, design and <em className='type-serif text-pop'>Webflow build.</em>
                    </SplitReveal>

                    <p className='type-lead mt-8 max-w-[44ch] text-quiet'>
                        You work directly with the people planning, designing and building your website. Decisions stay connected, and you know who is responsible at each stage.
                    </p>

                    <ol ref={listRef} className='mt-14'>
                        {whyPoints.map((point, index) => (
                            <li key={point.title} data-point='' className='relative grid grid-cols-[3rem_1fr] gap-x-4 py-7'>
                                <span data-rule='' aria-hidden='true' className='absolute inset-x-0 top-0 h-px origin-left bg-rule' />

                                <span data-point-body='' className='type-label pt-1.5 text-quiet'>
                                    0{index + 1}
                                </span>

                                <div data-point-body=''>
                                    <h3 className='type-h3'>
                                        {point.title}
                                    </h3>

                                    <p className='mt-3 max-w-[46ch] text-quiet'>
                                        {point.text}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>

            <div className='mt-24 grid items-center gap-10 border-t border-rule pt-10 lg:mt-32 lg:grid-cols-12'>
                <p className='type-label text-quiet lg:col-span-3'>
                    Independent recognition
                </p>

                <ul className='flex flex-wrap items-center gap-x-12 gap-y-8 lg:col-span-9 lg:justify-end'>
                    {awards.map(award => (
                        <li key={award.src}>
                            <img src={award.src} alt={award.alt} loading='lazy' decoding='async' className='h-11 w-auto opacity-80 mix-blend-multiply' />
                        </li>
                    ))}

                    <li className='type-label border border-rule px-3 py-2'>
                        Clutch Champions 2025
                    </li>
                </ul>
            </div>
        </section>
    )
}

export default Why
